// "Is this still available to buy?" - asked right when the buyer clicks Buy Now / Proceed to checkout,
// from the live database rather than the (possibly stale) page data.
//
// Same rules the product pages already use to show the Buy Now button:
//  - an artwork is available when it has no availability status (Sold / Gift / Reserved are not)
//  - a set is available when it has none, or "Partial Sold" (some pieces are still left)

export const isArtworkAvailable = (status: string | null | undefined) => !status;
export const isSetAvailable = (status: string | null | undefined) => !status || status === "Partial Sold";

type Entry = { found: boolean; availability: string | null };

export interface AvailabilityResult {
  /** The check itself worked (false = network/server problem, nothing is known). */
  checked: boolean;
  /** Everything asked about is available. */
  ok: boolean;
  artworks: Record<string, Entry & { available: boolean }>;
  sets: Record<string, Entry & { available: boolean }>;
}

export const useAvailabilityCheck = () => {
  const isChecking = ref(false);

  async function check(ids: { artworkIds?: string[]; setIds?: (string | number)[] }): Promise<AvailabilityResult> {
    isChecking.value = true;
    try {
      const data = await $fetch<{ artworks: Record<string, Entry>; sets: Record<string, Entry> }>(
        "/api/availability/check",
        { method: "POST", body: { artworkIds: ids.artworkIds ?? [], setIds: ids.setIds ?? [] } },
      );
      const artworks: AvailabilityResult["artworks"] = {};
      const sets: AvailabilityResult["sets"] = {};
      for (const [id, e] of Object.entries(data.artworks ?? {})) artworks[id] = { ...e, available: e.found && isArtworkAvailable(e.availability) };
      for (const [id, e] of Object.entries(data.sets ?? {})) sets[id] = { ...e, available: e.found && isSetAvailable(e.availability) };
      const all = [...Object.values(artworks), ...Object.values(sets)];
      return { checked: true, ok: all.length > 0 && all.every((e) => e.available), artworks, sets };
    } catch {
      return { checked: false, ok: false, artworks: {}, sets: {} };
    } finally {
      isChecking.value = false;
    }
  }

  return { isChecking, check };
};
